import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { ChartBar, Users, CurrencyCircleDollar, Stethoscope, PawPrint } from "@phosphor-icons/react";
import { toast } from "sonner";
import DatePickerZoom from "@/components/DatePickerZoom";

export default function AnalyticsPage() {
  const { t } = useLang();
  const [range, setRange] = useState({ from_date: "", to_date: "" });
  const [data, setData] = useState(null);

  async function load() {
    if (range.from_date && range.to_date && range.to_date < range.from_date) {
      return;
    }
    try {
      const params = Object.fromEntries(Object.entries(range).filter(([, v]) => v));
      const { data } = await api.get("/analytics/summary", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [range]);

  function setFrom(v) {
    setRange((r) => {
      const next = { ...r, from_date: v };
      if (r.to_date && v && r.to_date < v) {
        toast.error(t("invalidDateRange"));
        next.to_date = "";
      }
      return next;
    });
  }
  function setTo(v) {
    if (range.from_date && v && v < range.from_date) {
      toast.error(t("invalidDateRange"));
      return;
    }
    setRange((r) => ({ ...r, to_date: v }));
  }

  if (!data) return <div className="text-sm text-pf-text-secondary">{t("loading")}</div>;

  const dayMax = Math.max(1, ...data.consults_by_day.map((d) => d.count));

  return (
    <div className="animate-fade-in-up">
      <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="text-3xl font-display font-extrabold text-pf-text">{t("analytics")}</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="w-[168px]">
            <label className="pf-label mb-1 block">{t("dateFrom")}</label>
            <DatePickerZoom testId="analytics-from" value={range.from_date} onChange={setFrom} />
          </div>
          <div className="w-[168px]">
            <label className="pf-label mb-1 block">{t("dateTo")}</label>
            <DatePickerZoom testId="analytics-to" value={range.to_date} onChange={setTo} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-5">
        <Metric icon={<Stethoscope size={18} className="text-pf-primary" />} label={t("analyticsConsultations")} value={data.total_consultations} testid="an-consults" />
        <Metric icon={<Users size={18} className="text-pf-accent" />} label={t("analyticsPatientsSeen")} value={data.patients_seen} testid="an-patients" />
        <Metric icon={<PawPrint size={18} className="text-pf-blue" />} label={t("analyticsSpeciesTracked")} value={data.species_breakdown.length} testid="an-species" />
        <Metric icon={<CurrencyCircleDollar size={18} className="text-pf-success" />} label={t("analyticsRevenuePaid")} value={`${data.revenue_total.toFixed(2)}`} sub={`${data.paid_invoice_count} ${t("analyticsInvoices")}`} testid="an-revenue" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-8 pf-card p-5">
          <div className="pf-section-title mb-3 flex items-center gap-2"><ChartBar size={16} /> {t("analyticsLast14Days")}</div>
          <div className="flex items-end gap-1 h-40" data-testid="an-daily-chart">
            {data.consults_by_day.map((d) => {
              const height = (d.count / dayMax) * 100;
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.count}`}>
                  <div className="w-full flex items-end justify-center h-full">
                    <div
                      className="w-full rounded-t bg-pf-primary/70 hover:bg-pf-primary transition-colors"
                      style={{ height: `${Math.max(4, height)}%` }}
                    />
                  </div>
                  <span className="text-[9px] text-pf-text-secondary">{d.date.slice(5)}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-4 pf-card p-5">
          <div className="pf-section-title mb-3">{t("analyticsSpeciesBreakdown")}</div>
          <div className="space-y-2">
            {data.species_breakdown.length === 0 && <div className="text-sm text-pf-text-secondary">{t("analyticsNoData")}</div>}
            {data.species_breakdown.map((s) => {
              const total = data.species_breakdown.reduce((a, b) => a + b.count, 0);
              const pct = total ? (s.count / total) * 100 : 0;
              const speciesLabel = s.species === "dog" ? t("dog") : s.species === "cat" ? t("cat") : s.species;
              return (
                <div key={s.species}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="capitalize font-medium text-pf-text">{speciesLabel}</span>
                    <span className="text-pf-text-secondary">{s.count}</span>
                  </div>
                  <div className="h-2 bg-pf-bg rounded-full overflow-hidden">
                    <div className="h-full bg-pf-primary" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-6 pf-card p-5">
          <div className="pf-section-title mb-3">{t("analyticsTopReasons")}</div>
          <ul className="space-y-2">
            {data.top_visit_reasons.length === 0 && <div className="text-sm text-pf-text-secondary">{t("analyticsNoData")}</div>}
            {data.top_visit_reasons.map((r) => (
              <li key={r.reason} className="flex justify-between items-center text-sm">
                <span className="text-pf-text">{r.reason}</span>
                <span className="pf-chip-primary">{r.count}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-6 pf-card p-5">
          <div className="pf-section-title mb-3">{t("analyticsTopDiagnoses")}</div>
          <ul className="space-y-2">
            {data.top_diagnoses.length === 0 && <div className="text-sm text-pf-text-secondary">{t("analyticsNoData")}</div>}
            {data.top_diagnoses.map((d, i) => (
              <li key={i} className="flex justify-between items-center text-sm gap-2">
                <span className="text-pf-text truncate">{d.diagnosis === "__undiagnosed__" ? t("undiagnosedYet") : d.diagnosis}</span>
                <span className="pf-chip">{d.count}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-12 pf-card p-5">
          <div className="pf-section-title mb-3">{t("analyticsByVet")}</div>
          <div className="space-y-2">
            {data.consults_by_vet.length === 0 && <div className="text-sm text-pf-text-secondary">{t("analyticsNoData")}</div>}
            {data.consults_by_vet.map((v) => {
              const max = Math.max(1, ...data.consults_by_vet.map((x) => x.count));
              const pct = (v.count / max) * 100;
              return (
                <div key={v.vet_id}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-pf-text font-medium">{v.name}</span>
                    <span className="text-pf-text-secondary">{v.count}</span>
                  </div>
                  <div className="h-2 bg-pf-bg rounded-full overflow-hidden">
                    <div className="h-full bg-pf-accent" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ icon, label, value, sub, testid }) {
  return (
    <div className="pf-card p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="pf-label">{label}</div>
        {icon}
      </div>
      <div className="mt-2 text-2xl font-display font-bold text-pf-text">{value}</div>
      {sub && <div className="text-xs text-pf-text-secondary mt-1">{sub}</div>}
    </div>
  );
}
